import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { environment } from '../../../environments/environment';

const REFRESH_TOKEN_PATH = '/auth/refresh-token';

// Toda stack de telemetria declara seu endpoint num campo `*LogURL` do environment
// (orderFlowLogURL, multiSignalLogURL, mpBannerLogURL — e a próxima que existir).
// Derivar a isenção do PRÓPRIO environment, em vez de manter uma lista aqui, é a
// diferença entre "cobre as stacks de hoje" e "cobre a stack que alguém criar amanhã":
// a lista à mão já ficou desatualizada uma vez (api-fail.interceptor.ts:44-45 isenta
// order-flow e multi-signal, e esqueceu o banner). Casar por domínio também não serve:
// os endpoints são execute-api com ID sorteado por stack e em dev são localhost.
const TELEMETRY_URL_KEY = /LogURL$/;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    constructor(public authService: AuthenticationService) { }

    // Refresh em voo, compartilhado: vários 401 simultâneos renovam o token UMA vez e todos
    // reenviam com o token novo. Volta a null ao terminar (sucesso ou falha), então um 401
    // posterior começa um refresh novo em vez de esperar por um ciclo que já acabou.
    private refreshInFlight: Observable<string> | null = null;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (this.authService.getJwtToken()) {
            request = this.addToken(request, this.authService.getJwtToken());
        }

        return next.handle(request).pipe(catchError(error => {
            // O próprio refresh fica de fora: 401 nele é a sessão morta, não um pedido de
            // renovação. Sem essa guarda o refresh chamaria refresh e travaria o ciclo.
            // Telemetria também fica fora do ciclo de refresh: ela não manda mais
            // token, então um 401 vindo dali não fala nada sobre a sessão. Se falasse,
            // um authorizer mal configurado no API Gateway derrubaria o cliente logado
            // no meio do checkout — telemetria pode se perder, nunca deslogar ninguém.
            const expiredToken = error instanceof HttpErrorResponse
                && error.status === 401
                && !request.url.includes(REFRESH_TOKEN_PATH)
                && !this.isTelemetryUrl(request.url);

            if (expiredToken) {
                return this.handle401Error(request, next);
            }

            // 403 NÃO derruba a sessão. Aqui havia um `logout()` para qualquer 403 de
            // qualquer endpoint, e ele deslogava o cliente já autenticado: 403 nesta API
            // quer dizer "esta ação não é permitida", nunca "seu token morreu" (sem token o
            // AuthMiddleware responde 400, expirado 401, inválido 500). O caso de produção
            // foi o cardápio: `GET /restaurant/{id}/discount-coupons/featured` roda em TODA
            // carga do menu e responde 403 MODULE_DISABLED em loja sem o módulo new_coupon,
            // então quem logava e navegava voltava deslogado. Quem prova que a sessão
            // acabou é o refresh, tratado em handle401Error.
            return throwError(error);
        }));
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.sharedRefresh().pipe(
            take(1),
            // catchError ANTES do switchMap de propósito: só a falha do REFRESH desloga.
            // Depois do switchMap ele pegaria também o erro da request reenviada, e aí um
            // 500 qualquer no retry derrubaria a sessão.
            catchError(error => {
                this.authService.logout();
                return throwError(error);
            }),
            switchMap(token => next.handle(this.addToken(request, token))),
        );
    }

    private sharedRefresh(): Observable<string> {
        if (this.refreshInFlight) return this.refreshInFlight;

        this.refreshInFlight = this.authService.refreshToken().pipe(
            map((data: any) => data && data.token),
            // tap ANTES do shareReplay: roda uma vez, junto com a fonte, e não por subscriber.
            tap({
                next: () => { this.refreshInFlight = null; },
                error: () => { this.refreshInFlight = null; },
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        return this.refreshInFlight;
    }

    private addToken(request: HttpRequest<any>, token: string) {
        if(request.url.includes('viacep')) return request;
        if (this.isTelemetryUrl(request.url)) return request;
        return request.clone({
            setHeaders: {
                'Authorization': `Bearer ${token}`
            }
        });
    }

    /**
     * A request é para uma stack de telemetria?
     *
     * Isentar é PRIMEIRO uma correção de segurança: o JWT emitido para o cliente
     * daquele restaurante não tem nada que fazer num endpoint de telemetria. Ele era
     * enviado para três API Gateways que não pedem, não validam e não deveriam ver
     * credencial nenhuma — e o que trafega acaba em log de acesso. Telemetria é
     * anônima por contrato (o payload é redigido); mandar o token junto contradizia
     * o contrato pelo header.
     *
     * O efeito colateral é o que trouxe a questão à tona: o AllowHeaders do API
     * Gateway aceita só `content-type`, então o preflight com `authorization` volta
     * 204 SEM header algum de CORS e o browser BLOQUEIA o POST. Medido contra
     * produção: sem `authorization` o OPTIONS responde `access-control-allow-origin: *`;
     * com ele, nada. Como o erro morria no `subscribe({ error: () => {} })` do
     * transporte, o canal POST ficava permanentemente morto em todo device que já
     * tinha logado naquela loja (token em `token-<restaurantID>`) — ~1 em cada 5 da
     * coorte. O `sendBeacon` escapava por mandar Blob `text/plain`, content-type
     * safelisted que não dispara preflight; por isso a perda era só do POST.
     */
    private isTelemetryUrl(url: string): boolean {
        if (!url) return false;
        for (const endpoint of this.telemetryEndpoints()) {
            if (url.indexOf(endpoint) === 0) return true;
        }
        return false;
    }

    /**
     * Lido a cada request, não uma vez no boot: o environment é um objeto mutável e
     * os specs das stacks trocam esses campos em tempo de execução. São poucas
     * chaves, e o custo some perto de uma ida à rede.
     */
    private telemetryEndpoints(): string[] {
        const endpoints: string[] = [];
        try {
            for (const key of Object.keys(environment)) {
                if (!TELEMETRY_URL_KEY.test(key)) continue;
                const value = (environment as any)[key];
                // String vazia = stack desligada naquele ambiente (green/blue/beta
                // declaram ''). Sem esta guarda o `indexOf === 0` casaria com QUALQUER
                // URL e o app inteiro deixaria de autenticar.
                if (typeof value === 'string' && value.length > 0) endpoints.push(value);
            }
        } catch (e) {
            // Descobrir a isenção nunca pode quebrar a request que a pediu.
        }
        return endpoints;
    }
}
